import { Navigate } from 'react-router-dom';
import { useParams, useLocation } from "react-router";
import LegacyEventReports from "./LegacyEventReports.jsx";
import EventReports from "./EventReports.jsx";
import ScheduledReports from "./ScheduledReports.jsx";
import WellnessReports from "./WellnessReports.jsx";
import SessionReload from "./component-assets/SessionReload.jsx";
import HourlyReports from "./HourlyReports.jsx";
import ExecutiveReport from "./ExecutiveReport.jsx";
import EnvironmentReports from "./EnvironmentReports.jsx";
import MaintenanceReports from "./MaintenanceReports.jsx";
import {compareRole, iAmHigherAuthority} from "../libs.jsx";
import MaintenanceCloudReports from "./MaintenanceCloudReports.jsx";
import { usePortalVersion } from '../contexts/PortalVersionContext.jsx';
import AlertDetailsPage from '../pages/reports/alert-details/AlertDetailsPage.jsx';
import HourlySummaryPage from '../pages/reports/hourly-summary/HourlySummaryPage.jsx';
import MaintenanceReportPage from '../pages/reports/maintenance/MaintenanceReportPage.jsx';
import WellnessReportsPage, { scopeForReportType } from '../pages/reports/wellness/WellnessReportsPage.jsx';
import EnvironmentalReportsPage from '../pages/reports/environmental/EnvironmentalReportsPage.jsx';
import ScheduledReportsPage from '../pages/reports/scheduled/ScheduledReportsPage.jsx';
import ExecutiveReportPage from '../pages/reports/executive/ExecutiveReportPage.jsx';
import CallLogsReportPage from '../pages/reports/call-logs/CallLogsReportPage.jsx';
import CaregiverReportPage from '../pages/reports/caregiver/CaregiverReportPage.jsx';
import ShiftReportPage from '../pages/reports/shifts/ShiftReportPage.jsx';
import { getSession } from "../services/session";

const Reports = (props) => {
    const { portalVersion } = usePortalVersion();
    const isV2 = portalVersion === 'v2';
    let reportContent, dData, paramVars, bData;
    paramVars = useParams();
    // Legacy /reports/wellness carries the report type in nav state; use it to pick
    // which of the two split pages to show. Hook call stays unconditional.
    const legacyLocation = useLocation();
    const wellnessLegacyScope = scopeForReportType(legacyLocation?.state?.subType) || 'checkin';

    dData = getSession();

    if (dData?.migrated){
        if (paramVars.dType === "alerts"){
            reportContent = isV2
                ? <AlertDetailsPage run={paramVars?.eType === "run"} />
                : <EventReports toFetch={"alerts"} run={paramVars?.eType === "run"} />;
        }else if (paramVars.dType === "caregiver"){
            /* V2 only. The non-migrated branch further down already routes
               "caregiver" to LegacyEventReports; that stays as it is. */
            reportContent = <CaregiverReportPage run={paramVars?.eType === "run"} />;
        }else if (paramVars.dType === "shifts"){
            /* No V1 counterpart. "shifts" is a NEW dType -- unlike "caregiver",
               which reuses a V1 name and so needs a LegacyEventReports branch
               below. Nothing falls through to V1 for this report, which is why
               there is no matching entry in the non-migrated block. */
            reportContent = <ShiftReportPage run={paramVars?.eType === "run"} />;
        }else if (paramVars.dType === "maintenance"){
            if (isV2) {
                reportContent = <MaintenanceReportPage />;
            } else if (dData?.campusData[dData?.acnt.toString()]?.is_cloud){
                reportContent = <MaintenanceCloudReports />
            }else{
                reportContent = <MaintenanceReports />;
            }
        }else if (paramVars.dType === "checkin"){
            reportContent = isV2 ? <WellnessReportsPage scope="checkin" /> : <WellnessReports />;
        }else if (paramVars.dType === "rounds"){
            reportContent = isV2 ? <WellnessReportsPage scope="rounds" /> : <WellnessReports />;
        }else if (paramVars.dType === "wellness"){
            // Legacy route, kept so existing bookmarks and any not-yet-updated
            // "Run Report Local" links keep working. V2 picks the scope from the
            // requested subType so a Rounds schedule still lands on Rounds.
            reportContent = isV2
                ? <WellnessReportsPage scope={wellnessLegacyScope} />
                : <WellnessReports />;
        }else if (paramVars.dType === "scheduled"){
            reportContent = isV2 ? <ScheduledReportsPage /> : <ScheduledReports />;
        }else if (paramVars.dType === "grouped"){
            reportContent = isV2 ? <HourlySummaryPage /> : <HourlyReports />;
        }else if (paramVars.dType === "hourly"){
            reportContent = isV2 ? <HourlySummaryPage /> : <HourlyReports />;
        }else if (paramVars.dType === "executive"){
            reportContent = isV2 ? <ExecutiveReportPage /> : <ExecutiveReport />;
        }else if (paramVars.dType === "environmental"){
            reportContent = isV2 ? <EnvironmentalReportsPage /> : <EnvironmentReports />;
        }else if (paramVars.dType === "call-logs"){
            // Call Logs report — V2-only, gated on the campus grandstream flag.
            // Non-grandstream campuses can't reach it; redirect to alerts.
            const isGrandstream = dData?.campusData?.[dData?.acnt?.toString()]?.is_grandstream;
            reportContent = (isV2 && isGrandstream)
                ? <CallLogsReportPage />
                : <Navigate to="/reports/alerts" />;
        }else{
            reportContent = (
                <div>
                    <h3>Page not found. 404</h3>
                </div>
            );
        }
    }else{
        if (paramVars.dType === "alerts"){
            reportContent = <LegacyEventReports toFetch={"alerts"}/>;
        }
        else if (paramVars.dType === "resident"){
            reportContent = <LegacyEventReports toFetch={"resident"}/>;
        }
        else if (paramVars.dType === "caregiver"){
            reportContent = <LegacyEventReports toFetch={"caregiver"}/>;
        }
        else if (paramVars.dType === "maintenance"){
            reportContent = <LegacyEventReports toFetch={"maintenance"}/>;
        }
        else if (paramVars.dType === "inspection"){
            reportContent = <LegacyEventReports toFetch={"inspection"}/>;
        }
        else if (paramVars.dType === "checkin-transaction"){
            reportContent = <LegacyEventReports toFetch={"checkin-transaction"}/>;
        }
        else if (paramVars.dType === "checkin-history"){
            reportContent = <LegacyEventReports toFetch={"checkin-history"}/>;
        }
        else{
            reportContent = (
                <div>
                    <h3>Page not found. 404</h3>
                </div>
            );
        }
    }

    return (compareRole("caregiver", dData?.userrole) || iAmHigherAuthority(10, dData?.role_weight)) ? (
        <>
            {dData?.migrated && <SessionReload />}
            {reportContent}
        </>
    ) : <Navigate to={"/"}/>;
};

export default Reports;